import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MessageCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Camera,
  ZoomIn,
} from "lucide-react";
import { PROJECTS, whatsappLink } from "@/lib/business";

/** Flatten all project images into one array for the lightbox */
const flattenImages = (project) =>
  project.stages.flatMap((stage) =>
    stage.images.map((img) => ({ ...img, stageLabel: stage.label }))
  );

const StageSection = ({ stage, onOpen, projectImages, startIndex, imageMode }) => {
  const isFit = imageMode === "fit";
  const aspectClass = isFit
    ? "aspect-[3/5] sm:aspect-[2/3]"
    : "aspect-[4/3]";
  const objectClass = isFit ? "object-contain" : "object-cover";
  const overlayClass = isFit
    ? "bg-black/10 group-hover:bg-black/0"
    : "bg-black/30 group-hover:bg-black/10";
  const scaleClass = isFit ? "group-hover:scale-[1.02]" : "group-hover:scale-105";

  return (
    <div
      className="border-t border-[#27272A] first:border-t-0"
      data-testid={`stage-${stage.id}`}
    >
      <div className="px-6 md:px-8 py-5 flex items-center gap-3">
        <span
          className="block w-8 h-[3px]"
          style={{ background: stage.accent }}
        />
        <span className="font-display text-xs tracking-[0.3em] text-white">
          {stage.label.toUpperCase()}
        </span>
        <span className="ml-auto font-body text-xs text-[#A1A1AA]">
          {stage.images.length} photo{stage.images.length > 1 ? "s" : ""}
        </span>
      </div>
      {stage.description && (
        <p className="px-6 md:px-8 -mt-2 mb-5 text-sm text-[#d4d4d8] font-body leading-relaxed">
          {stage.description}
        </p>
      )}
      <div className="px-4 md:px-6 pb-6">
        <div
          className={`grid gap-3 ${
            stage.images.length === 1
              ? "grid-cols-1"
              : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          }`}
        >
          {stage.images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onOpen(projectImages, startIndex + i)}
              className={`relative group overflow-hidden border border-[#27272A] hover:border-[#E60000] ${aspectClass} focus:outline-none focus:border-[#E60000] bg-[#050505]`}
              data-testid={`stage-${stage.id}-img-${i}`}
            >
              <img
                src={img.url}
                alt={img.alt}
                loading="lazy"
                className={`w-full h-full ${objectClass} ${scaleClass} transition-transform duration-500`}
              />
              <div
                className={`absolute inset-0 ${overlayClass} transition-colors pointer-events-none`}
              />
              <span className="absolute top-2 left-2 bg-black/80 text-white font-display text-[10px] tracking-widest px-2 py-1 pointer-events-none">
                {stage.label.toUpperCase()}
              </span>
              <span className="absolute bottom-2 right-2 inline-flex items-center justify-center w-8 h-8 bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <ZoomIn className="w-4 h-4" />
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProjectCard = ({ project, onOpen }) => {
  const allImages = flattenImages(project);
  let running = 0;
  return (
    <article
      className="border border-[#27272A] bg-[#0a0a0a]"
      data-testid={`project-${project.id}`}
    >
      {/* Header */}
      <div className="p-6 md:p-8 border-b border-[#27272A]">
        <div className="flex items-center gap-3 mb-3">
          <Camera className="w-4 h-4 text-[#FFCC00]" />
          <span className="font-display text-xs tracking-[0.3em] text-[#FFCC00]">
            FEATURED PROJECT
          </span>
        </div>
        <h3 className="font-display text-2xl md:text-4xl text-white leading-tight">
          {project.title}
        </h3>
        <div className="mt-2 font-display text-sm tracking-widest text-[#E60000]">
          {project.service.toUpperCase()}
        </div>
        <p className="mt-4 text-[#d4d4d8] leading-relaxed max-w-3xl">
          {project.summary}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {project.tags.map((t) => (
            <span
              key={t}
              className="border border-[#27272A] text-[#d4d4d8] font-display tracking-widest text-[11px] px-3 py-1.5"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Stages */}
      {project.stages.map((stage) => {
        const startIndex = running;
        running += stage.images.length;
        return (
          <StageSection
            key={stage.id}
            stage={stage}
            onOpen={onOpen}
            projectImages={allImages}
            startIndex={startIndex}
            imageMode={project.imageMode}
          />
        );
      })}
    </article>
  );
};

const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  const img = images[index];
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  if (!img) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-md flex items-center justify-center"
      data-testid="gallery-lightbox"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white hover:text-[#FFCC00] p-2 z-10"
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X className="w-8 h-8" />
      </button>

      {images.length > 1 && (
        <>
          <button
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-[#E60000] p-3 z-10 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-[#E60000] p-3 z-10 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <div
        className="relative max-w-[95vw] max-h-[92vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.url}
          alt={img.alt}
          className="max-w-[95vw] max-h-[80vh] object-contain border border-[#27272A]"
        />
        <div className="mt-4 flex items-center gap-3 text-center">
          {img.stageLabel && (
            <span className="bg-[#E60000] text-white font-display text-[10px] tracking-widest px-3 py-1">
              {img.stageLabel.toUpperCase()}
            </span>
          )}
          <span className="font-body text-xs text-[#d4d4d8]">
            {index + 1} / {images.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export const Gallery = () => {
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });

  const openLightbox = useCallback((images, index) => {
    setLightbox({ open: true, images, index });
    document.body.style.overflow = "hidden";
  }, []);
  const closeLightbox = useCallback(() => {
    setLightbox((s) => ({ ...s, open: false }));
    document.body.style.overflow = "";
  }, []);
  const next = useCallback(
    () =>
      setLightbox((s) => ({
        ...s,
        index: (s.index + 1) % s.images.length,
      })),
    []
  );
  const prev = useCallback(
    () =>
      setLightbox((s) => ({
        ...s,
        index: (s.index - 1 + s.images.length) % s.images.length,
      })),
    []
  );

  return (
    <div data-testid="gallery-page" className="bg-[#050505]">
      <section className="relative pt-16 pb-16 md:pt-24 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00]">
            / GALLERY
          </div>
          <h1 className="mt-4 font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9]">
            THE WORK.
            <br />
            <span className="text-[#E60000]">SPEAKS LOUD.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#d4d4d8] leading-relaxed">
            Every project, documented from start to finish. Tap any photo to
            zoom in and swipe through the full build.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col gap-10">
          {PROJECTS.map((p) => (
            <ProjectCard key={p.id} project={p} onOpen={openLightbox} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] border-t border-[#27272A] py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div>
            <div className="font-display text-xs tracking-[0.4em] text-[#FFCC00] mb-2">
              IMPRESSED?
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-white">
              LET'S PAINT <span className="text-[#E60000]">YOURS</span>.
            </h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="gallery-cta-quote"
              className="inline-flex items-center gap-3 bg-[#FFCC00] hover:bg-white text-black font-display tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-1"
            >
              REQUEST QUOTE <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-cta-whatsapp"
              className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-display tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-1"
            >
              <MessageCircle className="w-4 h-4" /> WHATSAPP
            </a>
          </div>
        </div>
      </section>

      {lightbox.open && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={closeLightbox}
          onPrev={prev}
          onNext={next}
        />
      )}
    </div>
  );
};

export default Gallery;
